import React from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { salutationTranslation } from "shared/lib/constants";
import { isValidBirthDay, safeFormatMessage } from "shared/lib/functions";
import { formattedMessageParams } from "shared/messages";

import { PhoneInput } from "@components/Forms/Inputs/CustomPhoneInput";
import DateInput from "@components/Forms/Inputs/DateInput";
import { checkRequired, isEmail, isPhoneNumber } from "@components/Forms/Rules";
import { Field, Select } from "@components/Profile/Forms/HooksForm";

import { AppState } from "@shared/data/reducers";

const {
  givenNameText,
  givenNamePlaceholder,
  surnameText,
  surnamePlaceholder,
  telephoneText,
  emailText,
  emailPlaceholder,
  birthdateText,
} = defineMessages({
  givenNameText: formattedMessageParams("Profile.givenName"),
  givenNamePlaceholder: formattedMessageParams("Profile.givenNamePlaceholder"),
  surnameText: formattedMessageParams("Profile.surname"),
  surnamePlaceholder: formattedMessageParams("Profile.surnamePlaceholder"),
  telephoneText: formattedMessageParams("Form.telephone"),
  emailText: formattedMessageParams("Profile.email"),
  emailPlaceholder: formattedMessageParams("Profile.emailPlaceholder"),
  birthdateText: formattedMessageParams("Profile.birthdate"),
});

const PassengerForm = ({ formVals, setFormVals, setErrors, errors }) => {
  const intl = useIntl();
  const passengerPrefixes = useSelector(
    (state: AppState) => state.storage.passengerPrefix
  );

  return (
    <>
      <Field
        required
        name="givenName"
        id="traveler-namePrefix"
        label={intl.formatMessage({ id: "Profile.salutation" })}
        placeholder={intl.formatMessage({ id: "Profile.salutation" })}
        value={formVals.namePrefix}
        error={errors.namePrefix}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, namePrefix: err }));
          setFormVals((state) => ({
            ...state,
            namePrefix: newVal,
          }));
        }}
        component={Select}
        options={passengerPrefixes.map(({ value }) => ({
          value,
          label: safeFormatMessage({
            intl,
            id: salutationTranslation[value],
            fallback: value,
          }),
        }))}
      />
      <Field
        required
        name="givenName"
        id="traveler-givenName"
        label={intl.formatMessage(givenNameText)}
        placeholder={intl.formatMessage(givenNamePlaceholder)}
        value={formVals.givenName}
        error={errors.givenName}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, givenName: err }));
          setFormVals((state) => ({ ...state, givenName: newVal }));
        }}
      />
      <Field
        required
        name="surname"
        id="traveler-surname"
        label={intl.formatMessage(surnameText)}
        placeholder={intl.formatMessage(surnamePlaceholder)}
        value={formVals.surname}
        error={errors.surname}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, surname: err }));
          setFormVals((state) => ({ ...state, surname: newVal }));
        }}
      />
      <Field
        required
        name="telephone"
        id="traveler-telephone"
        label={intl.formatMessage(telephoneText)}
        component={PhoneInput}
        value={formVals.telephone}
        error={errors.telephone}
        setter={(newVal) => {
          const err = checkRequired(newVal) || isPhoneNumber(newVal);
          setErrors((state) => ({ ...state, telephone: err }));
          setFormVals((state) => ({ ...state, telephone: newVal }));
        }}
      />
      <Field
        required
        name="email"
        id="traveler-email"
        label={intl.formatMessage(emailText)}
        placeholder={intl.formatMessage(emailPlaceholder)}
        value={formVals.email}
        error={errors.email}
        setter={(newVal) => {
          const err = checkRequired(newVal) || isEmail(newVal);
          setErrors((state) => ({ ...state, email: err }));
          setFormVals((state) => ({ ...state, email: newVal }));
        }}
      />
      <Field
        name="traveler-birthDate"
        id="traveler-birthDate"
        component={DateInput}
        showTop
        value={formVals.birthDate}
        error={errors.birthDate}
        setter={(newVal) => {
          const [day, month, year] = newVal?.split("-")?.reverse() || [];
          const err = !isValidBirthDay(day, month, year) && (
            <FormattedMessage id="Profile.wrongFilledDate" />
          );
          setErrors((state) => ({
            ...state,
            birthDate: err,
          }));
          setFormVals((state) => ({
            ...state,
            birthDate: newVal,
          }));
        }}
        label={intl.formatMessage(birthdateText)}
      />
    </>
  );
};

export default PassengerForm;
